import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import ProductDetailModal from "../components/ProductDetailModal";
import BarcodeScannerModal from "../components/BarcodeScannerModal";
import MapBarcodeModal from "../components/MapBarcodeModal";
import { isPromoActive, isNewActive, promoExpiryLabel } from "../utils/promo";
import { getAllProducts } from "../services/dataCache";
import { getCustomerPricing, specialPriceFor } from "../services/customerPricingService";
import { findByBarcode } from "../services/barcodeService";
import { productMatches, productMatchesRelaxed, relaxedScore } from "../utils/searchMatch";
import FloatingDictation from "../components/FloatingDictation";
import { useLang } from "../i18n/LanguageContext";

function ceilTo2(n) {
  return Math.ceil(n * 100) / 100;
}

// 40891 연두부 sorts as if it were 10099 so the WHOLE tofu family groups
// together at the top of the catalog (Brian, 2026-09-29) — right after
// 10098 순두부, before 30009. Sort-only alias; the displayed SKU is unchanged.
const CATALOG_SORT_ALIAS = { "40891": 10099 };

function skuCompare(a, b) {
  const numA = CATALOG_SORT_ALIAS[a.sku] ?? parseInt(a.sku, 10);
  const numB = CATALOG_SORT_ALIAS[b.sku] ?? parseInt(b.sku, 10);
  const aIsNum = !isNaN(numA) && /^\d+/.test(a.sku);
  const bIsNum = !isNaN(numB) && /^\d+/.test(b.sku);
  if (aIsNum && bIsNum) return numA - numB;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.sku.localeCompare(b.sku);
}

export default function Catalog({ onAddToCart }) {
  const { profile } = useAuth();
  const { t } = useLang();
  const isStaff = profile?.role === "rep" || profile?.role === "admin";
  // Per-customer price visibility (backlog #4): hides ONLY prices — stock,
  // promos-as-filters, and ordering all keep working. Staff always see prices.
  const showPrices = isStaff || profile?.showPrices !== false;
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [view, setView] = useState("all"); // all | promo | new (one-click filters)
  const [loading, setLoading] = useState(true);
  // qtyInputs holds ONLY quantities the user has touched (stepper/typed).
  // That set drives the "Add all" button; entries are removed after adding.
  const [qtyInputs, setQtyInputs] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [mapBarcode, setMapBarcode] = useState(null); // unknown scanned code (staff only)
  const [toast, setToast] = useState(null);
  const [queueOpen, setQueueOpen] = useState(false); // review-queued-items popup

  // Store-specific special pricing (2026-08-24): a customer linked to a
  // store sees THEIR price — base struck + special in red, same styling as
  // promos. Staff browse at base prices (their cart applies the store's
  // pricing once a customer is picked); display only — the order
  // transaction reprices live at submit.
  const myRecordId = !isStaff ? (profile?.customerRecordId || null) : null;
  const [myPricing, setMyPricing] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!myRecordId) { setMyPricing(null); return; }
    getCustomerPricing(myRecordId)
      .then(d => { if (alive) setMyPricing(d); })
      .catch(() => { if (alive) setMyPricing(null); });
    return () => { alive = false; };
  }, [myRecordId]);

  const specialFor = (sku) => specialPriceFor(myPricing, sku);

  function popToast(message, duration = 2000) {
    setToast(message);
    setTimeout(() => setToast(null), duration);
  }

  useEffect(() => {
    async function load() {
      const all = await getAllProducts(); // session cache — 0 reads on revisit
      // Keep EVERYTHING: browsing shows active items only, but searching
      // also reveals inactive/zero-stock ones (Brian, 2026-07-14 — the daily
      // CSV auto-deactivates zero-stock items, and those must stay orderable).
      setProducts([...all].sort(skuCompare));
      setLoading(false);
    }
    load();
  }, []);

  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();

  const promoCount = products.filter(p => isPromoActive(p)).length;
  const newCount = products.filter(p => isNewActive(p)).length;

  // New-items popup (Brian, 2026-09-11): once per device per 24h, so customers
  // actually SEE new arrivals (the ✨ chip alone was easy to miss). Any close
  // starts the 24h snooze. Rides on isNewActive — expired "new" flags never pop.
  const NEW_POPUP_SNOOZE_KEY = "sunjin-newpopup-snoozed-until";
  const [newPopupOpen, setNewPopupOpen] = useState(false);
  const newPopupCheckedRef = useRef(false);
  useEffect(() => {
    if (newPopupCheckedRef.current || products.length === 0) return;
    newPopupCheckedRef.current = true; // check once per visit, not per cache refresh
    if (!products.some(p => isNewActive(p) && !p.keepHidden)) return;
    try {
      const until = parseInt(localStorage.getItem(NEW_POPUP_SNOOZE_KEY) || "0", 10);
      if (Date.now() < until) return;
    } catch { /* storage blocked — just show it */ }
    setNewPopupOpen(true);
  }, [products]);

  function closeNewPopup(showThem) {
    setNewPopupOpen(false);
    try {
      localStorage.setItem(NEW_POPUP_SNOOZE_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch { /* not persistable — popup just shows again next visit */ }
    if (showThem) setView("new");
  }

  // Tap a product IN the popup -> real product modal (order from there, same
  // as the catalog). Closing that modal returns to the popup — no snooze —
  // so they can keep browsing the new items or close for real (Brian: people
  // don't read, they press pictures).
  const [returnToNewPopup, setReturnToNewPopup] = useState(false);
  function openNewPopupProduct(p) {
    setReturnToNewPopup(true);
    setNewPopupOpen(false);
    setSelectedProduct(p);
  }
  function handleProductModalClose() {
    setSelectedProduct(null);
    if (returnToNewPopup) {
      setReturnToNewPopup(false);
      setNewPopupOpen(true);
    }
  }

  const searching = search.trim().length > 0;
  // Regularly-carried SKU families (Brian, 2026-08-20): these stay visible
  // while BROWSING even when out of stock (orderable as backorders), so
  // customers can find and order them without searching. Other prefixes —
  // old/discontinued ranges (CJ, SB, ST, 77, ...) — remain search-only.
  const BROWSE_OOS_PREFIXES = /^(?:[1-5]|slp|w)/i;
  // Inactive items outside the carried SKU families only surface when the
  // user is actively searching. Items MARKED discontinued (bulk sheet on
  // admin Products, 2026-08-20) are search-only even inside those families.
  // Matching is punctuation/space/sound-blind (utils/searchMatch); when the
  // exact search finds NOTHING, we retry relaxed — compound words split into
  // keywords ("간장게장" → 간장 + 게장) so close matches show instead of a
  // dead end (Brian, 2026-08-24).
  const passesFilters = (p, matcher) => {
    // keepHidden (Brian, 2026-08-26): customers NEVER see these — browsing,
    // searching, or scanning, in stock or not (the daily CSV doesn't clear
    // the mark, unlike discontinued). Staff still find them via search.
    if (p.keepHidden && !(isStaff && searching)) return false;
    if (!searching && !p.active && !(BROWSE_OOS_PREFIXES.test(p.sku) && !p.discontinued)) return false;
    const matchesBrand = brandFilter === "all" || p.brand === brandFilter;
    const matchesView =
      view === "all" ||
      (view === "promo" && isPromoActive(p)) ||
      (view === "new" && isNewActive(p));
    return matcher(p, search) && matchesBrand && matchesView;
  };
  let filtered = products.filter(p => passesFilters(p, productMatches));
  const closeMatchesShown = searching && filtered.length === 0;
  if (closeMatchesShown) {
    // Close matches rank by how much of the query they cover — the intended
    // product first, generic keyword hits after (Brian, 2026-08-24).
    filtered = products
      .filter(p => passesFilters(p, productMatchesRelaxed))
      .sort((a, b) => relaxedScore(b, search) - relaxedScore(a, search) || skuCompare(a, b));
  }
  // Plain SKU order everywhere, in stock or not (Brian, 2026-08-21: an
  // OOS-last sort buried staples like 10001 at the bottom of browsing —
  // items must stay findable in their usual spot).

  // Quantities start at 0 (Brian, 2026-07-10): tapping + makes it 1, which
  // feels like actually building an order. A qty of 0 removes the entry, so
  // qtyInputs holds exactly the products with a real quantity — that set
  // drives the Add-all bar and each card's Add button.
  function getQty(sku) {
    return qtyInputs[sku] ?? 0;
  }

  function setQty(sku, qty) {
    setQtyInputs(prev => {
      const next = { ...prev };
      if (qty <= 0) delete next[sku];
      else next[sku] = qty;
      return next;
    });
  }

  // No upper cap: as long as an item is in stock at all, customers may order
  // beyond the stock level (the excess is backordered). Out-of-stock items
  // stay blocked via the disabled controls below.
  function stepQty(sku, delta) {
    setQty(sku, Math.max(0, getQty(sku) + delta));
  }

  function setQtyDirect(sku, value) {
    const n = parseInt(value, 10);
    setQty(sku, isNaN(n) ? 0 : Math.max(0, n));
  }

  function handleAdd(p) {
    const qty = getQty(p.sku);
    if (qty <= 0) return; // button is disabled at 0; belt-and-suspenders
    onAddToCart(p, qty);
    setQty(p.sku, 0); // back to untouched — keeps Add-all honest
    popToast(t("added", qty, p.name));
  }

  // "Add all": every product whose qty the user touched, in one tap.
  const touchedSkus = Object.keys(qtyInputs);
  function handleAddAll() {
    let added = 0;
    for (const sku of touchedSkus) {
      const p = products.find(x => x.sku === sku);
      if (!p) continue;
      onAddToCart(p, qtyInputs[sku]);
      added++;
    }
    setQtyInputs({});
    popToast(t("addedAll", added));
  }

  async function handleBarcodeScanned(code) {
    setShowScanner(false);
    try {
      // Cache-based lookup (0 reads) across ALL products — including
      // inactive ones, so a scan always reveals what a code is mapped to.
      const all = await getAllProducts();
      const hit = findByBarcode(all, code);
      // keepHidden items scan as "no match" for customers (staff still get
      // the product — they may need to check what a code maps to).
      if (hit && hit.keepHidden && !isStaff) {
        popToast(t("noBarcodeMatch", code), 4000);
        return;
      }
      if (!hit) {
        if (isStaff) {
          setMapBarcode(String(code)); // offer to map it on the spot
        } else {
          popToast(t("noBarcodeMatch", code), 4000);
        }
        return;
      }
      setSelectedProduct(hit);
    } catch (err) {
      popToast(t("barcodeLookupFailed", err.message), 4000);
    }
  }

  function handleMapped(p) {
    setMapBarcode(null);
    popToast(t("mappedTo", p.name), 3000);
    // Refresh the mapped product into the catalog list if it's shown there
    setProducts(prev => prev.map(x => x.sku === p.sku ? { ...x, barcode: p.barcode } : x));
    setSelectedProduct(p); // open it so the scan can turn into an order
  }

  if (loading) return <p>{t("loadingCatalog")}</p>;

  return (
    <div className="catalog">
      <div className="catalog-controls">
        <input
          placeholder={t("searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
          <option value="all">{t("allBrands")}</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <button className="scan-btn" onClick={() => setShowScanner(true)}>
          {t("scanBarcode")}
        </button>
      </div>

      {(promoCount > 0 || newCount > 0) && (
        <div className="catalog-view-chips">
          <button
            className={`view-chip${view === "all" ? " active" : ""}`}
            onClick={() => setView("all")}
          >{t("allItems")}</button>
          {promoCount > 0 && (
            <button
              className={`view-chip promo-chip${view === "promo" ? " active" : ""}`}
              onClick={() => setView(view === "promo" ? "all" : "promo")}
            >{t("promotionsChip", promoCount)}</button>
          )}
          {newCount > 0 && (
            <button
              className={`view-chip new-chip${view === "new" ? " active" : ""}`}
              onClick={() => setView(view === "new" ? "all" : "new")}
            >{t("newItemsChip", newCount)}</button>
          )}
        </div>
      )}

      {touchedSkus.length > 0 && (
        <div className="add-all-bar">
          <span>{t("itemsWithQty", touchedSkus.length)}</span>
          {/* Review the queue (Brian, 2026-08-24, from the field): see every
              item with a typed quantity in one list — fix a misclick or drop
              an item without hunting its card down again. */}
          <button className="mini-btn" onClick={() => setQueueOpen(true)}>
            {t("reviewQueued")}
          </button>
          <button className="add-all-btn" onClick={handleAddAll}>
            {t("addAllToCart", touchedSkus.length)}
          </button>
        </div>
      )}

      {queueOpen && touchedSkus.length > 0 && (
        <div className="modal-overlay" onClick={() => setQueueOpen(false)}>
          <div className="modal-content order-sheet-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setQueueOpen(false)}>×</button>
            <h3>{t("queuedItemsTitle", touchedSkus.length)}</h3>
            <table className="packing-table">
              <thead>
                <tr><th>{t("sku")}</th><th>{t("product")}</th><th className="qty-col">{t("qty")}</th><th></th></tr>
              </thead>
              <tbody>
                {[...touchedSkus].sort((a, b) => {
                  const na = CATALOG_SORT_ALIAS[a] ?? parseInt(a, 10);
                  const nb = CATALOG_SORT_ALIAS[b] ?? parseInt(b, 10);
                  if (!isNaN(na) && !isNaN(nb)) return na - nb;
                  return a.localeCompare(b);
                }).map(sku => {
                  const p = products.find(x => x.sku === sku);
                  return (
                    <tr key={sku}>
                      <td>{sku}</td>
                      <td>{p ? p.name : sku}</td>
                      <td className="qty-col">
                        <input
                          type="number"
                          min={1}
                          className="free-qty-input"
                          value={getQty(sku)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            // ignore blank/0 while typing — Remove is the
                            // explicit way to drop a row from the queue
                            const n = parseInt(e.target.value, 10);
                            if (!isNaN(n) && n >= 1) setQty(sku, n);
                          }}
                        />
                      </td>
                      <td>
                        <button className="mini-btn danger" onClick={() => setQty(sku, 0)}>
                          {t("remove")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button
              className="add-all-btn"
              onClick={() => { handleAddAll(); setQueueOpen(false); }}
            >
              {t("addAllToCart", touchedSkus.length)}
            </button>
          </div>
        </div>
      )}

      {newPopupOpen && (
        <div className="modal-overlay" onClick={() => closeNewPopup(false)}>
          <div className="modal-content new-popup" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => closeNewPopup(false)}>×</button>
            <h3>{t("newPopupTitle")}</h3>
            <div className="new-popup-grid">
              {products.filter(p => isNewActive(p) && !p.keepHidden).slice(0, 12).map(p => (
                <div key={p.sku} className="new-popup-card clickable" onClick={() => openNewPopupProduct(p)}>
                  {p.imageUrl
                    ? <img src={p.imageUrl} alt={p.name} loading="lazy" />
                    : <div className="new-popup-noimg">{t("noPhoto")}</div>}
                  <p>{p.name}</p>
                </div>
              ))}
            </div>
            <button className="new-popup-view" onClick={() => closeNewPopup(true)}>
              {t("newPopupView")}
            </button>
            <button className="mini-btn" onClick={() => closeNewPopup(false)}>
              {t("newPopupClose")}
            </button>
          </div>
        </div>
      )}

      {closeMatchesShown && filtered.length > 0 && (
        <p className="backorder-note">{t("closeMatches")}</p>
      )}

      <div className="product-grid">
        {filtered.map(p => {
          const displayStock = ceilTo2(p.stockQty);
          const wholeStock = Math.floor(p.stockQty);
          const qty = getQty(p.sku);
          const backordered = wholeStock > 0 && qty > p.stockQty;
          return (
            <div key={p.sku} className="product-card">
              {p.imageUrl
                ? <img src={p.imageUrl} alt={p.name} loading="lazy" onClick={() => setSelectedProduct(p)} className="clickable" />
                : <div className="no-image clickable" onClick={() => setSelectedProduct(p)}>{t("noPhoto")}</div>}
              <p className="sku">
                {t("sku")}: {p.sku}
                {/* staff-only by construction: customers never receive keepHidden cards */}
                {p.keepHidden && <span className="hidden-badge"> 🚫 hidden</span>}
              </p>
              {p.barcode && <p className="barcode-line">{t("barcode")}: {p.barcode}</p>}
              <h4 className="clickable" onClick={() => setSelectedProduct(p)}>
                {isNewActive(p) && <span className="new-badge">{t("newBadge")}</span>}
                {p.name}
              </h4>
              <p className="brand">{p.brand}</p>
              {showPrices && (
                specialFor(p.sku) != null ? (
                  <p className="price">
                    <s className="price-struck">${p.basePrice.toFixed(2)}</s>{" "}
                    <span className="promo-price">${specialFor(p.sku).toFixed(2)} CAD</span>
                  </p>
                ) : isPromoActive(p) ? (
                  <p className="price">
                    <s className="price-struck">${p.basePrice.toFixed(2)}</s>{" "}
                    <span className="promo-price">${p.promoPrice.toFixed(2)} CAD</span>
                  </p>
                ) : (
                  <p className="price">${p.basePrice.toFixed(2)} CAD</p>
                )
              )}
              {isPromoActive(p) && promoExpiryLabel(p) && (
                <p className="short-date-note">{t("shortDate", promoExpiryLabel(p))}</p>
              )}
              <p className={wholeStock > 0 ? "in-stock" : "out-of-stock"}>
                {wholeStock > 0 ? t("inStock", displayStock) : t("outOfStock")}
              </p>
              {/* Out-of-stock items stay orderable (Brian, 2026-07-14):
                  system stock and warehouse reality drift, so the order is
                  taken and backordered rather than blocked. */}
              <div className="qty-stepper-row">
                <button
                  className="qty-btn"
                  onClick={() => stepQty(p.sku, -1)}
                >−</button>
                <input
                  type="number"
                  className="qty-value-input"
                  min={0}
                  value={qty}
                  onChange={(e) => setQtyDirect(p.sku, e.target.value)}
                />
                <button
                  className="qty-btn"
                  onClick={() => stepQty(p.sku, 1)}
                >+</button>
              </div>
              {qty > 0 && wholeStock <= 0 ? (
                <p className="backorder-note">{t("oosNote")}</p>
              ) : backordered && (
                <p className="backorder-note">
                  {t("backorderNote", displayStock)}
                </p>
              )}
              <button
                className="add-btn"
                disabled={qty === 0}
                title={qty === 0 ? t("tapPlusFirst") : undefined}
                onClick={() => handleAdd(p)}
              >
                {t("addToOrder")}
              </button>
            </div>
          );
        })}
      </div>
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          specialPrice={specialFor(selectedProduct.sku)}
          onClose={handleProductModalClose}
          onAddToCart={(p, qty) => {
            onAddToCart(p, qty);
            popToast(t("added", qty, p.name));
          }}
        />
      )}
      {/* One floating mic for the whole page — speech goes to the search box */}
      <FloatingDictation onText={(text) => setSearch(text)} />
      {toast && <div className="toast">{toast}</div>}
      {showScanner && (
        <BarcodeScannerModal
          onScan={handleBarcodeScanned}
          onClose={() => setShowScanner(false)}
        />
      )}
      {mapBarcode && (
        <MapBarcodeModal
          code={mapBarcode}
          onClose={() => setMapBarcode(null)}
          onMapped={handleMapped}
        />
      )}
    </div>
  );
}
